// The outbound call state machine (SPEC §15.1). Pure: no DOM, no I/O, so it is unit-tested in tests/fd-*.test.ts.
//
//   idle ──DIAL──▶ preparing ──PREPARED──▶ connecting ──RINGING──▶ ringing ──ACCEPTED──▶ in_call
//     ▲              │   │                    │  (ACCEPTED straight from connecting is allowed)        │
//     │   PREPARE_FAILED  PREPARE_BLOCKED     CONNECT_FAILED / ENDED            ENDED ◀─────────────────┘
//     │   PREPARE_NEEDS_CONFIRM (→ idle: a 409 a yes would pass; the store asks "Call this number again?")
//     │              ▼   ▼                    ▼                                  │
//     └───────────── idle blocked ◀─DISMISS   idle                               ▼
//                                                                               ended ──PENDING_CHECKED──▶ needs_disposition ──SAVED──▶ idle
//                                                                                    └────────(nothing to log)──────────────────────▶ idle
//
// A voicemail drop can finish after the browser leg hangs up (the Worker answers /drop only after logging it, while
// Twilio ends the browser leg as soon as the PSTN leg is redirected). So `dropping` survives ENDED, and DROP_FINISHED
// is applied in 'ended' (the store waits for it before asking /pending) and in 'needs_disposition' (→ idle: logged).
//
// Every transition not drawn above is ignored (the same object is returned), which is what makes a double keypress,
// a stale Twilio event or a late HTTP response harmless: a setter can never dial twice or skip a disposition.
import type { DispositionCode, GateResult } from '../../../src/shared/api';

export type CallPhase =
  | 'idle'              // a lead may be on screen; nothing in flight
  | 'preparing'         // POST /api/calls/prepare in flight
  | 'blocked'           // prepare answered 409 gate_blocked; reasons on screen, Skip offered
  | 'connecting'        // device.connect() called; waiting for Twilio
  | 'ringing'           // the prospect's line is ringing
  | 'in_call'           // answered (answerOnBridge: the browser leg opens when the PSTN leg answers)
  | 'ended'             // call over; deciding whether a disposition is owed
  | 'needs_disposition';// the setter must log the outcome before the next lead

export interface CallMachine {
  phase: CallPhase;
  membershipId: number | null;
  callId: number | null;
  to: string | null;
  callerId: string | null;
  gate: GateResult | null;           // set in 'blocked'
  error: string | null;              // last plain-English problem, shown under the call bar
  reachedTwilio: boolean;            // ringing or answered was seen (Twilio answered the browser leg)
  placed: boolean;                   // 'ringing' was seen: <Dial> reached their line, so an attempt was placed. A refusal
                                     // (<Say>…</Say><Hangup/> from /twilio/voice) answers the browser leg without ringing.
  answered: boolean;
  startedAt: number | null;          // ms epoch, set on PREPARED
  answeredAt: number | null;
  endedAt: number | null;
  endReason: string | null;
  saving: boolean;                   // disposition POST in flight
  saveError: string | null;          // why the last disposition save failed (shown in the wrap-up dock)
  serverDisposition: DispositionCode | null; // already stored server-side (meeting booking or voicemail drop)
  dropping: boolean;                 // voicemail drop POST in flight
}

export type CallEvent =
  | { type: 'DIAL'; membershipId: number }
  | { type: 'PREPARED'; callId: number; to: string; callerId: string; now: number }
  | { type: 'PREPARE_BLOCKED'; gate: GateResult }
  | { type: 'PREPARE_NEEDS_CONFIRM' }                 // 409 that a yes would pass (PJ 2026-10-20): back to idle, the store asks
  | { type: 'PREPARE_FAILED'; message: string }
  | { type: 'CONNECT_FAILED'; message: string }
  | { type: 'RINGING' }
  | { type: 'ACCEPTED'; now: number }
  | { type: 'ENDED'; now: number; reason?: string; error?: string }
  | { type: 'PENDING_CHECKED'; serverPending: boolean | null } // null = the check itself failed
  | { type: 'RESTORE_PENDING'; callId: number; membershipId: number; to?: string | null } // to: the number it went to (PJ 2026-10-23)
  | { type: 'DROP_STARTED' }
  | { type: 'DROP_FINISHED'; ok: boolean; message?: string }
  | { type: 'SERVER_DISPOSED'; code: DispositionCode }
  | { type: 'SAVE_STARTED' }
  | { type: 'SAVE_FAILED'; message: string }
  | { type: 'CLEAR_SAVE_ERROR' }                   // the setter changed what the failed save was about (outcome, callback time)
  | { type: 'SAVED' }
  | { type: 'NOTHING_OWED'; message: string } // the server says there is nothing to log (409 not_placed)
  | { type: 'DISMISS_BLOCK' }
  | { type: 'CLEAR_ERROR' }
  | { type: 'RESET' };

export const INITIAL_CALL: CallMachine = Object.freeze({
  phase: 'idle',
  membershipId: null,
  callId: null,
  to: null,
  callerId: null,
  gate: null,
  error: null,
  reachedTwilio: false,
  placed: false,
  answered: false,
  startedAt: null,
  answeredAt: null,
  endedAt: null,
  endReason: null,
  saving: false,
  saveError: null,
  serverDisposition: null,
  dropping: false,
}) as CallMachine;

/** Phases in which a live Twilio call may exist. */
export const LIVE_PHASES: readonly CallPhase[] = ['connecting', 'ringing', 'in_call'];

export function isLive(m: CallMachine): boolean {
  return LIVE_PHASES.includes(m.phase);
}

/** The only phases from which a new dial may start. */
export function canDial(m: CallMachine): boolean {
  return m.phase === 'idle' || m.phase === 'blocked';
}

/** Leaving the current lead (skip, open another, next) is allowed only when nothing is in flight or owed. */
export function canLeaveLead(m: CallMachine): boolean {
  return m.phase === 'idle' || m.phase === 'blocked';
}

/**
 * After a call ends: is a disposition owed?
 * - a voicemail drop already stored `vm_dropped` → no;
 * - a meeting booked during the call → yes (the wrap-up lets the setter add the note; re-saving within 10 min replaces);
 * - otherwise trust the server's `/api/calls/pending`; if that check failed, fall back to "did their line ring".
 *   Not `reachedTwilio`: a dial refused by /twilio/voice is answered with a <Say> (so ACCEPTED fires) but was never
 *   placed, and the server would refuse every outcome for it with 409 not_placed.
 */
export function dispositionOwed(m: CallMachine, serverPending: boolean | null): boolean {
  if (m.serverDisposition === 'vm_dropped') return false;
  if (m.serverDisposition === 'meeting_booked') return true;
  if (serverPending !== null) return serverPending;
  return m.placed;
}

export function transition(m: CallMachine, e: CallEvent): CallMachine {
  switch (e.type) {
    case 'DIAL':
      if (!canDial(m)) return m;
      return { ...INITIAL_CALL, phase: 'preparing', membershipId: e.membershipId };

    case 'PREPARED':
      if (m.phase !== 'preparing') return m;
      return { ...m, phase: 'connecting', callId: e.callId, to: e.to, callerId: e.callerId, startedAt: e.now };

    case 'PREPARE_BLOCKED':
      if (m.phase !== 'preparing') return m;
      return { ...m, phase: 'blocked', gate: e.gate };

    case 'PREPARE_NEEDS_CONFIRM':
      // Only soft attempt rules refused it: nothing was prepared; the lead is idle again while "Call this number again?"
      // is on screen (the store's dialog). Its Yes is a new DIAL.
      if (m.phase !== 'preparing') return m;
      return { ...m, phase: 'idle', gate: null };

    case 'PREPARE_FAILED':
      if (m.phase !== 'preparing') return m;
      return { ...m, phase: 'idle', error: e.message };

    case 'CONNECT_FAILED':
      // device.connect() rejected: Twilio never received the call, so no attempt was placed and nothing is owed.
      if (m.phase !== 'connecting') return m;
      return { ...m, phase: 'idle', callId: null, error: e.message };

    case 'RINGING':
      if (m.phase !== 'connecting') return m;
      return { ...m, phase: 'ringing', reachedTwilio: true, placed: true };

    case 'ACCEPTED':
      if (m.phase !== 'connecting' && m.phase !== 'ringing') return m;
      return { ...m, phase: 'in_call', reachedTwilio: true, answered: true, answeredAt: e.now };

    case 'ENDED':
      if (!isLive(m)) return m;
      // `dropping` is kept: the drop's HTTP reply usually lands after the browser leg ends (see the header).
      return { ...m, phase: 'ended', endedAt: e.now, endReason: e.reason ?? null, error: e.error ?? m.error };

    case 'PENDING_CHECKED':
      if (m.phase !== 'ended') return m;
      if (dispositionOwed(m, e.serverPending)) return { ...m, phase: 'needs_disposition' };
      return { ...INITIAL_CALL, membershipId: m.membershipId, error: m.error, serverDisposition: m.serverDisposition };

    case 'RESTORE_PENDING':
      // On page load /api/calls/pending found an undispositioned call: force the wrap-up before anything else.
      if (m.phase !== 'idle' && m.phase !== 'blocked') return m;
      return { ...INITIAL_CALL, phase: 'needs_disposition', callId: e.callId, membershipId: e.membershipId, reachedTwilio: true, placed: true, to: e.to ?? null };

    case 'DROP_STARTED':
      if (m.phase !== 'in_call' || m.dropping || m.serverDisposition) return m;
      return { ...m, dropping: true };

    case 'DROP_FINISHED':
      if (!m.dropping) return m;
      if (!e.ok) return { ...m, dropping: false, error: e.message ?? m.error };
      // The wrap-up was forced while the drop was still confirming: the server logged vm_dropped, so nothing is owed.
      // (If a save is in flight it will get 409 drop_final, which the store treats as "already logged".)
      if (m.phase === 'needs_disposition' && !m.saving) {
        return { ...INITIAL_CALL, membershipId: m.membershipId, serverDisposition: 'vm_dropped' };
      }
      return { ...m, dropping: false, serverDisposition: 'vm_dropped' };

    case 'SERVER_DISPOSED':
      if (m.callId === null) return m;
      return { ...m, serverDisposition: e.code };

    case 'SAVE_STARTED':
      if (m.phase !== 'needs_disposition' || m.saving) return m;
      return { ...m, saving: true, saveError: null };

    case 'SAVE_FAILED':
      if (m.phase !== 'needs_disposition') return m;
      return { ...m, saving: false, saveError: e.message };

    case 'CLEAR_SAVE_ERROR':
      if (m.phase !== 'needs_disposition' || m.saving || m.saveError === null) return m;
      return { ...m, saveError: null };

    case 'SAVED':
      if (m.phase !== 'needs_disposition') return m;
      return { ...INITIAL_CALL };

    case 'NOTHING_OWED':
      // The call never reached their line: back to idle on the same lead, which may be dialed again.
      if (m.phase !== 'needs_disposition') return m;
      return { ...INITIAL_CALL, membershipId: m.membershipId, error: e.message };

    case 'DISMISS_BLOCK':
      if (m.phase !== 'blocked') return m;
      return { ...m, phase: 'idle', gate: null };

    case 'CLEAR_ERROR':
      if (m.error === null) return m;
      return { ...m, error: null };

    case 'RESET':
      if (!canLeaveLead(m)) return m;
      return { ...INITIAL_CALL };
  }
}
